/* eslint-disable no-continue */
import { FieldErrors, FieldValues, RegisterOptions } from 'react-hook-form';
import {
  ErrorDisplayProps,
  ErrorObjectRegisterOption,
  ErrorObjectRegisterOptionAndService,
  FieldLabel,
  FormRules,
} from '../@types/forms';

const capitalize = (text: string) =>
  text.replace(/\b\w/g, (match) => match.toUpperCase());

const makeFriendlyNameFromFieldLabel = (label: FieldLabel): string => {
  // Build label for those with indexes
  const match = label.id.match(/\d+/);
  const index = match ? parseInt(match[0], 10) : null;
  if (index != null) {
    const sections = label.id.split('.');
    return sections.length > 0
      ? `${capitalize(sections[0])} ${index + 1}: ${label.text}`
      : label.text;
  }
  return label.text;
};

const mapObject = (
  cur: any,
  pre: ErrorObjectRegisterOption,
  errorObjectProps: string[],
  labels: FieldLabel[],
) => {
  const type = cur?.type as keyof RegisterOptions;
  const ref: { name: string } = cur?.ref;
  const label = labels.find((label) => {
    if (label.id.includes('.')) {
      return label.id === ref.name;
    } else if (ref && ref.name?.includes('.')) {
      const idSections = ref.name.split('.');
      const id = idSections.length ? idSections[0] : '';
      return id === label.id;
    }
    return label.id === ref.name;
  });
  const errorObject = {
    label: makeFriendlyNameFromFieldLabel(
      label || ({ id: '', text: '' } as FieldLabel),
    ),
    message: (cur?.message as string) || '',
  };
  if (type && errorObjectProps.includes(type)) {
    pre[type].push(errorObject);
  } else {
    pre.validate.push(errorObject);
  }
};

export const getFormStateErrors = <T extends FieldValues>(
  labels: FieldLabel[],
  rules: FormRules,
  errors: FieldErrors<T>,
): ErrorObjectRegisterOption => {
  const errorObject = {} as ErrorObjectRegisterOption;
  Object.keys(rules).forEach((field) => {
    if (!Object.hasOwnProperty.call(rules, field)) {
      return;
    }
    Object.keys(rules[field]).forEach((rule) => {
      if (!Object.hasOwnProperty.call(rules[field], rule)) {
        return;
      }
      const ruleKey = rule as keyof RegisterOptions;
      errorObject[ruleKey] = [];
    });
  });

  const errorObjectProps = Object.getOwnPropertyNames(errorObject);

  const rawFormStateErrors = Object.values(errors).reduce((pre, cur) => {
    const type = cur?.type as keyof RegisterOptions;
    if (type && errorObjectProps.includes(type)) {
      mapObject(cur, pre, errorObjectProps, labels);
    }
    return pre;
  }, errorObject);

  const formStateErrors =
    removeDuplicateElementsFromErrorObject(rawFormStateErrors);
  return formStateErrors;
};

export const mergeFormErrors = (
  errorObjects: ErrorObjectRegisterOptionAndService[],
): ErrorObjectRegisterOptionAndService => {
  const merged = {} as ErrorObjectRegisterOptionAndService;
  errorObjects.forEach((errorObj) => {
    Object.keys(errorObj).forEach((key) => {
      if (!Object.hasOwnProperty.call(errorObj, key)) {
        return;
      }
      const typedKey = key as keyof ErrorObjectRegisterOptionAndService;
      if (!merged[typedKey]) {
        merged[typedKey] = [];
      }
      merged[typedKey] = merged[typedKey].concat(errorObj[typedKey]);
    });
  });

  return merged;
};

export const joinFormErrorsWithServiceErrors = (
  formErrors: ErrorObjectRegisterOption,
  serviceErrors: string[],
): ErrorObjectRegisterOptionAndService => {
  const errors = {
    ...formErrors,
    service: Array.isArray(serviceErrors)
      ? serviceErrors.map((se) => ({ label: se, message: se }))
      : [],
  };
  return errors;
};

export const formHasErrorsToDisplay = (
  errors: ErrorObjectRegisterOptionAndService,
) => Object.values(errors).some((value) => value.length);

export const mapFormLabelsToFieldLabel = (
  labels: Record<string, string>,
): FieldLabel[] =>
  Object.entries(labels).map((entry) => ({
    id: entry[0],
    text: entry[1],
  }));

const removeDuplicateElementsFromErrorObject = (
  obj: ErrorObjectRegisterOption,
): ErrorObjectRegisterOption => {
  Object.keys(obj).forEach((key) => {
    if (!Object.hasOwnProperty.call(obj, key)) {
      return;
    }
    const typedKey = key as keyof ErrorObjectRegisterOption;
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key) && Array.isArray(obj[typedKey])) {
      const uniqueItems: ErrorDisplayProps[] = [];
      const seenLabels = new Set();

      obj[typedKey].forEach((item) => {
        if (!seenLabels.has(item.label)) {
          seenLabels.add(item.label);
          uniqueItems.push(item);
        }
      });

      // eslint-disable-next-line no-param-reassign
      obj[typedKey] = uniqueItems;
    }
  });
  return obj;
};
